/**
 * ResponseDistribution — Section 4: Response Time Distribution by Care Setting.
 * Four bar charts (AL, IL, MC, SNF) in a 2×2 grid.
 */

import { useRef, useEffect } from 'react';
import { Chart, registerables } from 'chart.js';

Chart.register(...registerables);

const BUCKET_LABELS = ['0-5', '5-10', '10-20', '20-60', '60+'];
const BAR_COLOR = '#2C7BE5';

const CHART_CONFIGS = [
    { key: 'al', label: 'Assisted Living' },
    { key: 'il', label: 'Independent Living' },
    { key: 'mc', label: 'Memory Care' },
    { key: 'snf', label: 'Skilled Care' },
];

const SingleChart = ({ dataArr, label }) => {
    const canvasRef = useRef(null);
    const chartRef = useRef(null);

    useEffect(() => {
        if (!canvasRef.current || !dataArr) return;

        const allZero = dataArr.every((v) => !v || v === 0);
        if (allZero) {
            if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; }
            return;
        }

        if (chartRef.current) {
            chartRef.current.data.datasets[0].data = dataArr;
            chartRef.current.update();
        } else {
            chartRef.current = new Chart(canvasRef.current, {
                type: 'bar',
                data: {
                    labels: BUCKET_LABELS,
                    datasets: [{
                        data: dataArr,
                        backgroundColor: BAR_COLOR,
                        borderRadius: 5,
                    }],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            callbacks: {
                                title: (items) => `${items[0].label} min`,
                                label: function (ctx) {
                                    const total = this.chart.data.datasets[0].data.reduce((s, v) => s + (v || 0), 0);
                                    const pct = total > 0 ? ((ctx.raw / total) * 100).toFixed(1) : 0;
                                    return [` ${ctx.formattedValue} Alarms`, ` ${pct}%`];
                                },
                            },
                        },
                    },
                    scales: {
                        x: { title: { display: true, text: 'Response Time (min)' }, grid: { display: false } },
                        y: { title: { display: true, text: 'Alarms' }, grid: { display: false } },
                    },
                },
            });
        }

        return () => {
            if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; }
        };
    }, [dataArr]);

    const allZero = !dataArr || dataArr.every((v) => !v || v === 0);
    if (allZero) return null;

    return (
        <div className="ex-chart-card">
            <h4 className="ex-chart-card__title">{label}</h4>
            <div className="ex-chart-card__canvas">
                <canvas ref={canvasRef} />
            </div>
        </div>
    );
};

const ResponseDistribution = ({ chartData }) => {
    if (!chartData) return null;

    const hasAny = CHART_CONFIGS.some(({ key }) => {
        const arr = chartData[key];
        return arr && arr.some((v) => v && v > 0);
    });

    if (!hasAny) return null;

    return (
        <div className="ex-section">
            <h3 className="ex-section__title">Response Time Distribution by Care Setting</h3>
            <div className="ex-chart-grid">
                {CHART_CONFIGS.map(({ key, label }) => (
                    <SingleChart key={key} dataArr={chartData[key] || []} label={label} />
                ))}
            </div>
        </div>
    );
};

export default ResponseDistribution;
